import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Wind, FlaskConical, ClipboardList, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ContactForm from '../components/ContactForm';
import { COMPANY, IMAGES, AIR_POLLUTANTS } from '../data/mock';

const AirQuality = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Indoor Air Quality Testing',
    provider: { '@type': 'LocalBusiness', name: '360 Mold Services', telephone: '+1-226-972-6440' },
    areaServed: 'Southern Ontario',
    description: 'Indoor air and surface sampling analyzed by accredited third-party labs to reveal mold spores and pollutants.',
  };
  const method = [
    { icon: Wind, title: 'Air Sampling', desc: 'We collect indoor air samples — plus an outdoor control — to compare spore levels against what’s normal.' },
    { icon: FlaskConical, title: 'Surface & Tape Lifts', desc: 'Tape-lift and swab samples from various surfaces are analyzed for precise, agreeable results.' },
    { icon: ClipboardList, title: 'Detailed Reporting', desc: 'A clear report outlining what we tested, your levels, target levels, and specific recommendations.' },
    { icon: ShieldCheck, title: 'Accredited Labs', desc: 'Analysis by A2LA-accredited (ISO/IEC 17025) third-party laboratories for unbiased accuracy.' },
  ];
  return (
    <div>
      <Seo
        path="/air-quality"
        title="Indoor Air Quality Testing"
        description="Independent indoor air quality testing across Southern Ontario. Air and surface sampling analyzed by accredited labs to reveal mold spores and hidden pollutants."
        schema={schema}
      />
      <section className="brand-gradient relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#c7e79b]/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">Indoor Air Quality Testing</span>
            <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#13251b] leading-tight">Know exactly what you’re breathing</h1>
            <p className="mt-5 text-lg text-[#44564b] leading-relaxed">The air in our homes, schools and offices can quietly affect our health. We combine computerized instruments, thermal imaging and lab analysis to fully investigate your indoor air — and pinpoint hidden issues.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="bg-brand hover:bg-[#5c9a22] text-white rounded-full px-7 h-12 font-semibold">
                <Link to="/contact">Test My Air <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 font-semibold border-[#cbdcbb] text-[#13251b] hover:bg-white">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> {COMPANY.phone}</a>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <img src={IMAGES.plantsInterior} alt="Fresh, plant-filled healthy home interior" className="rounded-[1.75rem] w-full h-[400px] object-cover shadow-xl" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">Why It Matters</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">Invisible pollutants, real health effects</h2>
            <p className="mt-4 text-[#5b6a60]">Poor indoor air can trigger sore eyes, headaches and fatigue — and worsen allergies, asthma and other long-term conditions.</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {AIR_POLLUTANTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 90} className="bg-[#fafcf7] rounded-2xl p-7 border border-[#eef2e8] hover-lift">
                  <div className="h-13 w-13 rounded-xl bg-[#eaf4dd] flex items-center justify-center p-3">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-[#13251b]">{p.title}</h3>
                  <p className="mt-2 text-[#5b6a60] leading-relaxed">{p.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-[#fafcf7]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.lab} alt="Laboratory sample analysis" className="rounded-[1.75rem] w-full h-[420px] object-cover shadow-xl" />
          </Reveal>
          <div>
            <Reveal>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#13251b]">How we test your air</h2>
              <p className="mt-4 text-[#5b6a60]">A combination of proven methods gives us a complete, objective picture — then we translate it into plain language you can act on.</p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {method.map((m, i) => {
                const Icon = m.icon;
                return (
                  <Reveal key={m.title} delay={i * 80} className="flex gap-4">
                    <div className="h-11 w-11 shrink-0 rounded-lg bg-[#eaf4dd] flex items-center justify-center">
                      <Icon className="h-5 w-5 text-brand" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#13251b]">{m.title}</h4>
                      <p className="mt-1 text-sm text-[#5b6a60] leading-relaxed">{m.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <Reveal className="bg-[#fafcf7] rounded-[1.5rem] p-7 lg:p-8 shadow-lg border border-[#eef2e8]">
            <h3 className="text-2xl font-bold text-[#13251b]">Request an air quality test</h3>
            <p className="mt-1 text-sm text-[#5b6a60] mb-6">Tell us about your space and any symptoms or concerns.</p>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AirQuality;
